/**
 * Migration Script: Update parent users created on January 21, 2026
 *
 * This script updates user documents that were created on January 21, 2026 to ensure
 * they have the correct parent role and school assignment.
 *
 * IMPORTANT: This script MUST be run in the browser console, not Node.js
 *
 * Usage:
 * 1. Open the app in your browser
 * 2. Open browser console
 * 3. Run: await window.migrateParentUsers()
 * 4. Review the dry-run results
 * 5. Run: await window.migrateParentUsers(true) to execute
 */

import { collection, getDocs, query, where, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

const TARGET_SCHOOL_ID = 'hktnikYSv6l6vGPbjMmG';

/**
 * Get the start and end timestamps for January 21, 2026
 * Uses expanded range to handle timezone differences (UTC+2 for South Africa)
 */
function getJanuary21Range() {
  // Start: January 20, 2026 22:00:00 UTC (2-hour buffer for UTC+2 timezone)
  // This ensures we catch users created at 00:00:00 on Jan 21 in UTC+2
  const startDate = new Date('2026-01-20T22:00:00.000Z');
  const startTimestamp = Timestamp.fromDate(startDate);

  // End: January 22, 2026 02:00:00 UTC (2-hour buffer for UTC+2 timezone)
  // This ensures we catch users created at 23:59:59 on Jan 21 in UTC+2
  const endDate = new Date('2026-01-22T02:00:00.000Z');
  const endTimestamp = Timestamp.fromDate(endDate);

  return { startTimestamp, endTimestamp };
}

/**
 * Query users created or updated on January 21, 2026
 */
async function queryTargetUsers() {
  const { startTimestamp, endTimestamp } = getJanuary21Range();
  const usersRef = collection(db, 'users');

  // Query by createdAt
  const createdQuery = query(
    usersRef,
    where('createdAt', '>=', startTimestamp),
    where('createdAt', '<=', endTimestamp)
  );

  // Query by updatedAt
  const updatedQuery = query(
    usersRef,
    where('updatedAt', '>=', startTimestamp),
    where('updatedAt', '<=', endTimestamp)
  );

  const [createdSnapshot, updatedSnapshot] = await Promise.all([
    getDocs(createdQuery),
    getDocs(updatedQuery)
  ]);

  // Combine results and deduplicate by UID
  const userMap = new Map();

  createdSnapshot.forEach(doc => {
    userMap.set(doc.id, { id: doc.id, ...doc.data() });
  });

  updatedSnapshot.forEach(doc => {
    if (!userMap.has(doc.id)) {
      userMap.set(doc.id, { id: doc.id, ...doc.data() });
    }
  });

  return Array.from(userMap.values());
}

/**
 * Determine if a user should be migrated
 */
function shouldMigrateUser(user) {
  // Skip users who are already staff or admin (these are actual teachers)
  if (user.staff === true || user.admin === true) {
    return false;
  }

  return true;
}

/**
 * Display dry-run results
 */
function displayDryRunResults(users) {
  console.log('\n' + '='.repeat(80));
  console.log('DRY RUN: Parent User Migration');
  console.log('='.repeat(80));
  console.log(`Found ${users.length} user(s) created/updated on January 21, 2026\n`);

  const usersToMigrate = users.filter(shouldMigrateUser);
  const usersToSkip = users.filter(u => !shouldMigrateUser(u));

  if (usersToMigrate.length === 0) {
    console.log('✅ No users need migration');
    return { usersToMigrate, usersToSkip };
  }

  console.log(`📝 ${usersToMigrate.length} user(s) will be updated:`);
  console.log(`⏭️  ${usersToSkip.length} user(s) will be skipped (staff/admin)\n`);

  // Show users that will be migrated
  usersToMigrate.forEach((user, index) => {
    console.log(`\n--- User ${index + 1} ---`);
    console.log(`UID: ${user.id}`);
    console.log(`Email: ${user.email || 'N/A'}`);
    console.log(`Name: ${user.displayName || user.firstName + ' ' + user.lastName || 'N/A'}`);
    console.log('\nCurrent values:');
    console.log(`  parent: ${user.parent}`);
    console.log(`  schoolId: ${user.schoolId || 'null'}`);
    console.log(`  schoolIds: ${JSON.stringify(user.schoolIds || [])}`);
    console.log(`  assignedSchoolId: ${user.assignedSchoolId || 'null'}`);
    console.log(`  staff: ${user.staff}`);
    console.log(`  admin: ${user.admin}`);
    console.log(`  dev: ${user.dev}`);
    console.log('\nNew values:');
    console.log(`  parent: true`);
    console.log(`  schoolId: "${TARGET_SCHOOL_ID}"`);
    console.log(`  schoolIds: ["${TARGET_SCHOOL_ID}"]`);
    console.log(`  staff: false`);
    console.log(`  admin: false`);
    console.log(`  dev: false`);
  });

  // Show users that will be skipped
  if (usersToSkip.length > 0) {
    console.log('\n' + '-'.repeat(80));
    console.log(`\n⏭️  Skipping ${usersToSkip.length} user(s) (staff/admin):\n`);
    usersToSkip.forEach((user, index) => {
      console.log(`${index + 1}. ${user.email} (${user.displayName || 'N/A'}) - staff: ${user.staff}, admin: ${user.admin}`);
    });
  }

  console.log('\n' + '='.repeat(80));
  console.log('\nTo execute this migration, run:');
  console.log('  await window.migrateParentUsers(true)');
  console.log('='.repeat(80) + '\n');

  return { usersToMigrate, usersToSkip };
}

/**
 * Execute the migration
 */
async function executeMigration(usersToMigrate) {
  console.log('\n' + '='.repeat(80));
  console.log('EXECUTING MIGRATION');
  console.log('='.repeat(80) + '\n');

  const results = {
    success: [],
    failed: []
  };

  for (const user of usersToMigrate) {
    try {
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        parent: true,
        schoolId: TARGET_SCHOOL_ID,
        schoolIds: [TARGET_SCHOOL_ID],
        staff: false,
        admin: false,
        dev: false,
        updatedAt: Timestamp.now()
      });

      results.success.push(user);
      console.log(`✅ Updated ${user.email}`);
    } catch (error) {
      results.failed.push({ user, error });
      console.error(`❌ Failed to update ${user.email}:`, error.message);
    }
  }

  console.log('\n' + '='.repeat(80));
  console.log('MIGRATION COMPLETE');
  console.log('='.repeat(80));
  console.log(`✅ Successfully updated: ${results.success.length}`);
  console.log(`❌ Failed: ${results.failed.length}`);
  console.log('='.repeat(80) + '\n');

  return results;
}

/**
 * Main migration function
 * @param {boolean} execute - If true, execute the migration. If false, dry-run only.
 */
export async function migrateParentUsers(execute = false) {
  try {
    console.log('🔍 Querying users...');
    const users = await queryTargetUsers();

    const { usersToMigrate, usersToSkip } = displayDryRunResults(users);

    if (!execute) {
      return {
        dryRun: true,
        usersToMigrate,
        usersToSkip
      };
    }

    // Execute migration
    if (usersToMigrate.length === 0) {
      console.log('No users to migrate.');
      return { dryRun: false, results: { success: [], failed: [] } };
    }

    const results = await executeMigration(usersToMigrate);
    return { dryRun: false, results };

  } catch (error) {
    console.error('❌ Migration failed:', error);
    throw error;
  }
}
